import React, { useEffect, useState } from "react";
import { digestApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Eye, Save, ChevronLeft, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

/** Renders a digest snapshot exactly as the email does — same order/blocks. */
function DigestView({ digest }) {
  if (!digest) return null;
  const snap = digest.snapshot || {};
  const health = snap.health_items || [];
  const tasks = snap.tasks || {};
  const upcoming = snap.upcoming || [];
  const decisions = snap.decisions || [];
  const fin = snap.financials || {};

  const Section = ({ title, children }) => (
    <div className="mb-5">
      <div className="text-eyebrow mb-2">{title}</div>
      <div className="text-sm text-white/85 space-y-1">{children}</div>
    </div>
  );
  const CcyRow = ({ label, obj }) => {
    const entries = Object.entries(obj || {});
    if (!entries.length) return null;
    return <div><span className="text-white/50">{label}:</span> {entries.map(([k, v]) => `${k} ${v.toLocaleString()}`).join(" · ")}</div>;
  };

  return (
    <div className="max-w-2xl">
      {digest.is_preview && <div className="mb-3 inline-block px-2.5 py-1 rounded bg-amber-500/15 border border-amber-400/30 text-amber-200 text-[11px] uppercase tracking-widest" data-testid="preview-banner">Preview — Not Sent</div>}
      <div className="text-eyebrow">Alias HQ</div>
      <h2 className="font-display text-2xl font-semibold">Weekly Executive Digest</h2>
      <div className="text-sm text-white/60 mb-3">{digest.period_start} — {digest.period_end}</div>
      <div className="text-white/90 mb-6 p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid="digest-exec-summary">{snap.executive_summary}</div>

      <Section title="Project Health">
        {health.length === 0 && <div className="text-white/50">All projects On Track.</div>}
        {health.map((h) => (
          <div key={h.project_id} className="p-2 rounded border border-white/10 bg-white/[0.02]" data-testid={`digest-health-${h.project_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border" style={{ color: h.status === "On Track" ? "#10B981" : (h.status === "Needs Attention" ? "#F59E0B" : "#EF4444"), borderColor: (h.status === "Needs Attention" ? "#F59E0B" : "#EF4444") + "55" }}>{h.status}</span>
              <span className="text-white/60">{h.entity_name}</span> ·
              <Link to={`/entity/${h.entity_id}?tab=projects`} className="text-white hover:underline">{h.project_name}</Link>
            </div>
            <div className="text-xs text-white/55 mt-0.5">{(h.reasons || []).slice(0, 3).join(" · ")}</div>
          </div>
        ))}
      </Section>

      <Section title="Tasks">
        {(!tasks.overdue?.length && !tasks.high_priority_this_week?.length) && <div className="text-white/50">Nothing urgent on the task list.</div>}
        {tasks.overdue?.length > 0 && <div className="text-red-400 font-medium text-xs mt-1">Overdue</div>}
        {tasks.overdue?.map(t => (
          <div key={t.id} data-testid={`digest-task-overdue-${t.id}`}>● <Link to="/tasks" className="hover:underline">{t.title}</Link> <span className="text-white/50 text-xs">· {t.due_date || ""}</span></div>
        ))}
        {tasks.high_priority_this_week?.length > 0 && <div className="text-amber-300 font-medium text-xs mt-2">High-priority this week</div>}
        {tasks.high_priority_this_week?.map(t => (
          <div key={t.id} data-testid={`digest-task-hp-${t.id}`}><Link to="/tasks" className="hover:underline">{t.title}</Link> <span className="text-white/50 text-xs">· {t.priority} · {t.due_date}</span></div>
        ))}
      </Section>

      {tasks.completed_this_period?.length > 0 && (
        <Section title="Recent Progress">
          {tasks.completed_this_period.map(t => (
            <div key={t.id} className="text-white/85" data-testid={`digest-task-done-${t.id}`}>✓ {t.title} <span className="text-white/50 text-xs">· {t.project_name || t.entity_name || ""}</span></div>
          ))}
        </Section>
      )}

      <Section title="This Week">
        {upcoming.length === 0 && <div className="text-white/50">No major events in the next 7 days.</div>}
        {upcoming.map((u, i) => (
          <div key={i} data-testid="digest-upcoming-row">{(u.starts_at || "").slice(0,10)} · {u.title} <span className="text-white/50 text-xs">· {u.entity_name || ""}</span></div>
        ))}
      </Section>

      <Section title="Decisions">
        {decisions.length === 0 && <div className="text-white/50">No open decisions.</div>}
        {decisions.map(d => (
          <div key={d.id} data-testid={`digest-decision-${d.id}`}>◇ {d.title} <span className="text-white/50 text-xs">· {d.entity_name || ""}</span></div>
        ))}
      </Section>

      <Section title="Money">
        <div className="space-y-1" data-testid="digest-money">
          <CcyRow label="Revenue received" obj={fin.revenue_by_currency}/>
          <CcyRow label="Expenses" obj={fin.expenses_by_currency}/>
          <CcyRow label="Outstanding revenue" obj={fin.outstanding_by_currency}/>
          {(!Object.keys(fin.revenue_by_currency || {}).length &&
            !Object.keys(fin.expenses_by_currency || {}).length &&
            !Object.keys(fin.outstanding_by_currency || {}).length) && <div className="text-white/50">No transaction activity this period.</div>}
        </div>
        {fin.budget_warnings?.length > 0 && (
          <div className="mt-2">
            <div className="text-amber-300 font-medium text-xs">Budget warnings</div>
            {fin.budget_warnings.map(w => (
              <div key={w.project_id} data-testid={`digest-budget-${w.project_id}`}>● {w.entity_name} · {w.project_name} at {w.pct}% ({w.currency})</div>
            ))}
          </div>
        )}
        {fin.upcoming_recurring?.length > 0 && (
          <div className="mt-2">
            <div className="text-white/60 text-xs">Upcoming recurring</div>
            {fin.upcoming_recurring.map(r => (
              <div key={r.id} data-testid={`digest-recurring-${r.id}`}>{r.next_run_date} · {r.kind} · {r.description} — {r.amount} {r.currency}</div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function SettingsPanel({ settings, onSaved }) {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(settings || {}), [settings]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const r = await digestApi.updateSettings({
        enabled: !!form.enabled,
        day_of_week: Number(form.day_of_week ?? 1),
        hour: Number(form.hour ?? 8),
        timezone: form.timezone || null,
        recipient_email: form.recipient_email || null,
      });
      toast.success("Settings saved");
      onSaved?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-lg space-y-4">
      <label className="flex items-center gap-2 text-sm text-white/85 cursor-pointer">
        <input type="checkbox" checked={!!form.enabled} onChange={e=>set("enabled", e.target.checked)} data-testid="digest-enabled"/>
        Send scheduled weekly digest
      </label>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-white/60 text-xs uppercase tracking-wider">Day of week</Label>
          <Select value={String(form.day_of_week ?? 1)} onValueChange={v=>set("day_of_week", Number(v))}>
            <SelectTrigger className="mt-1.5 bg-white/5 border-white/10" data-testid="digest-day"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-[#151A23] border-white/10 text-white">
              {DOW.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-white/60 text-xs uppercase tracking-wider">Hour (local)</Label>
          <Input type="number" min="0" max="23" value={form.hour ?? 8} onChange={e=>set("hour", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" data-testid="digest-hour"/>
        </div>
      </div>
      <div>
        <Label className="text-white/60 text-xs uppercase tracking-wider">Timezone (IANA, e.g. America/New_York)</Label>
        <Input value={form.timezone || ""} onChange={e=>set("timezone", e.target.value)} placeholder="Leave blank to use UTC" className="mt-1.5 bg-white/5 border-white/10" data-testid="digest-tz"/>
      </div>
      <div>
        <Label className="text-white/60 text-xs uppercase tracking-wider">Recipient email</Label>
        <Input type="email" value={form.recipient_email || ""} onChange={e=>set("recipient_email", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" data-testid="digest-recipient"/>
      </div>
      <div>
        <Button onClick={save} disabled={saving} data-testid="digest-save-settings" className="rounded-full bg-white text-black hover:bg-white/90">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Save className="w-4 h-4 mr-2"/>}
          Save settings
        </Button>
      </div>
    </div>
  );
}

export default function DigestPage() {
  const [tab, setTab] = useState("latest");
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, h, p] = await Promise.all([digestApi.getSettings(), digestApi.history(24), digestApi.preview()]);
      setSettings(s.data); setHistory(h.data || []); setPreview(p.data);
    } catch (e) { toast.error("Failed to load digest"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const doGenerate = async () => {
    try { const r = await digestApi.generate(); toast.success("Digest generated"); setSelected(r.data); const h = await digestApi.history(24); setHistory(h.data || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Generate failed"); }
  };
  const doSend = async (id) => {
    setSending(true);
    try { const r = await digestApi.send(id); toast[r.data.delivery_status === "sent" ? "success" : "error"](`Delivery ${r.data.delivery_status}`); const h = await digestApi.history(24); setHistory(h.data || []); setSelected(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };
  const doTest = async () => {
    setTesting(true);
    try { const r = await digestApi.testSend(); toast.success(`Test email sent to ${r.data.recipient}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Test send failed"); }
    finally { setTesting(false); }
  };

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden max-w-full" data-testid="digest-page">
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-5xl mx-auto">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-eyebrow">Executive Overview</div>
            <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-1">Weekly Digest</h1>
            <p className="text-sm text-white/60 mt-2 max-w-2xl">Automatically summarizes what needs your attention across Alias HQ. Delivered weekly, or preview any time.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={doGenerate} variant="outline" data-testid="digest-generate-btn" className="rounded-full border-white/15 hover:bg-white/10"><Eye className="w-3.5 h-3.5 mr-1.5"/>Save current as digest</Button>
            <Button onClick={doTest} disabled={testing} data-testid="digest-test-send-btn" className="rounded-full bg-white text-black hover:bg-white/90">
              {testing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Send className="w-3.5 h-3.5 mr-1.5"/>} Send test email
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-1 mb-6 border-b border-white/10 overflow-x-auto">
          {[{ k: "latest", l: "Latest preview" }, { k: "history", l: "History" }, { k: "settings", l: "Settings" }].map(t => (
            <button key={t.k} onClick={() => { setTab(t.k); setSelected(null); }}
                    className={`px-3 py-2 text-sm border-b-2 whitespace-nowrap ${tab === t.k ? "border-white text-white" : "border-transparent text-white/60 hover:text-white"}`}
                    data-testid={`digest-tab-${t.k}`}>{t.l}</button>
          ))}
        </div>

        {loading && <div className="text-white/50">Loading…</div>}

        {tab === "latest" && !loading && (
          <DigestView digest={preview} />
        )}

        {tab === "history" && !loading && (
          selected ? (
            <div>
              <button onClick={() => setSelected(null)} className="text-xs text-white/60 hover:text-white flex items-center gap-1 mb-3" data-testid="digest-back-btn"><ChevronLeft className="w-3.5 h-3.5"/> Back to history</button>
              <DigestView digest={selected}/>
              <div className="mt-6 flex gap-2 flex-wrap">
                <Button onClick={() => doSend(selected.id)} disabled={sending} data-testid="digest-send-btn" className="rounded-full bg-white text-black hover:bg-white/90">
                  {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Send className="w-3.5 h-3.5 mr-1.5"/>}
                  {selected.delivery_status === "sent" ? "Resend" : (selected.delivery_status === "failed" ? "Retry send" : "Deliver")}
                </Button>
                {selected.delivery_status && <span className="text-xs text-white/60 self-center">Last delivery: {selected.delivery_status}{selected.delivery_error ? ` — ${selected.delivery_error}` : ""}</span>}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {history.length === 0 && <div className="text-white/50 border border-dashed border-white/10 rounded-xl p-6 text-center">No digests yet. Use "Save current as digest" or wait for the scheduled delivery.</div>}
              {history.map(h => (
                <button key={h.id} onClick={() => setSelected(h)} data-testid={`digest-history-row-${h.id}`}
                        className="w-full text-left p-3 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-white font-medium">{h.period_start} — {h.period_end}</div>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-white/15 text-white/60">{h.delivery_status}</span>
                  </div>
                  <div className="text-xs text-white/50 mt-1 line-clamp-1">{h.snapshot?.executive_summary}</div>
                </button>
              ))}
            </div>
          )
        )}

        {tab === "settings" && !loading && (
          <SettingsPanel settings={settings} onSaved={setSettings}/>
        )}
      </div>
    </div>
  );
}
